import { useState } from "react";
import { useSubmitFeedback } from "@platform/hooks";
import { Button, useToast } from "@nthamizh/ui";
import { Bug, MessageSquare, X } from "lucide-react";

/**
 * Floating feedback/bug-report widget. Mounted once in AppShell, not
 * per-page — visible everywhere across the whole app, not just ConfigIQ,
 * per direction: feedback about the enterprise pages or role builder needs
 * somewhere to go too, not just tasks. Ported from Quantum Catalyst's
 * FeedbackWidget, adapted for @nthamizh/ui.
 *
 * The source's draftId prop (linking a report to the specific task open
 * when it was filed) is deliberately not carried over here — that only
 * made sense when the widget lived inside ConfigIQ's own page tree.
 * Mounted globally in Platform's shell, there's no reliable way to know
 * "the user is currently inside a ConfigIQ draft" without new plumbing
 * across the Module Federation boundary (e.g. ConfigIQ posting the
 * current draft id via a window event Platform listens for). Not built
 * here since it wasn't asked for and would add real fragility for a
 * nice-to-have — revisit if per-task linking turns out to matter.
 */
export function FeedbackWidget() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<"feedback" | "bug">("feedback");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const submit = useSubmitFeedback();

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;
    try {
      await submit.mutateAsync({ type, title: title.trim(), description: description.trim() });
      toast({ title: "Thank you! Your feedback has been submitted.", variant: "success" });
      setOpen(false);
      setTitle("");
      setDescription("");
      setType("feedback");
    } catch {
      toast({ title: "Could not submit feedback. Please try again.", variant: "error" });
    }
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() => setOpen(true)}
          className="group flex items-center gap-2 bg-white border border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all duration-150 rounded-full px-4 py-2.5 shadow-[0_2px_8px_0_rgb(0_0_0/0.12)] text-sm font-medium"
        >
          <MessageSquare className="h-4 w-4" />
          <span>Feedback</span>
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setOpen(false)} />

          <div className="relative bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">Share feedback</h3>
              <button
                onClick={() => setOpen(false)}
                className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setType("feedback")}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-all duration-100 ${
                    type === "feedback"
                      ? "border-blue-300 bg-blue-50 text-blue-700"
                      : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <MessageSquare className="h-4 w-4" />
                  Feedback
                </button>
                <button
                  onClick={() => setType("bug")}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-all duration-100 ${
                    type === "bug"
                      ? "border-red-300 bg-red-50 text-red-700"
                      : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <Bug className="h-4 w-4" />
                  Report a bug
                </button>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  {type === "bug" ? "What went wrong?" : "What's on your mind?"}
                </label>
                <input
                  type="text"
                  className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 transition-all"
                  placeholder={type === "bug" ? "Short summary of the issue…" : "Brief title…"}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  {type === "bug" ? "Steps to reproduce" : "Details"}
                </label>
                <textarea
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 transition-all resize-none"
                  rows={4}
                  placeholder={
                    type === "bug"
                      ? "1. Go to…\n2. Click…\n3. Expected: …\n4. Actual: …"
                      : "Any additional details or suggestions…"
                  }
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  loading={submit.isPending}
                  disabled={!title.trim() || !description.trim()}
                  className="flex-1"
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
